import { useState } from 'react';
import type { VehicleResult, OperatingBreakdown } from '../../types';
import { useAppModeStore } from '../../store/useAppModeStore';

// ── Colour palette (up to 4 vehicles) ─────────────────────────────────────────
// Phase 25: shifted off the bright dark-theme palette toward muted ledger tones.
// Each color is dark enough to read against the paper surface.
export const VEHICLE_COLORS = ['#17181A', '#0E7C5A', '#A23A2E', '#5B3A82'];

interface Props {
  vehicles: VehicleResult[];
  horizonMonths: number[];
  purchasePrices: number[];
}

function fmt$(v: number | undefined | null): string {
  const n = Number(v ?? 0);
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPerYear(total: number | null, horizonMonths: number): string {
  if (total === null) return '—';
  const years = horizonMonths / 12;
  const perYear = years > 0 ? total / years : 0;
  return '$' + Math.round(perYear).toLocaleString('en-US') + '/yr';
}

function fmtTotal(v: number | null): string {
  if (v === null) return '—';
  return '$' + Math.round(v).toLocaleString('en-US') + ' total';
}

function fmtPct(v: number | undefined | null): string {
  // Backend returns shares already as percentage values (e.g. 33.94 means 33.94%)
  return Number(v ?? 0).toFixed(1) + '%';
}

interface Row {
  label: string;
  values: (number | null)[];
  format: 'currency' | 'percent';
  lowerBetter: boolean; // true → lowest value = best (green)
  // Phase 21H: residual line is a credit (negative on the cost ledger).
  // Higher = better when isCredit=true.
  isCredit?: boolean;
  tooltip?: string;
}

function buildRows(vehicles: VehicleResult[], horizonMonths: number[]): Row[] {
  const get = (v: VehicleResult, section: string, key: string): number | null => {
    const sec = v[section as keyof VehicleResult] as Record<string, unknown> | null;
    if (!sec) return null;
    const val = sec[key];
    return typeof val === 'number' ? val : null;
  };

  return [
    {
      label: 'Horizon Months',
      values: horizonMonths.map((m) => m),
      format: 'currency',
      lowerBetter: false,
    },
    {
      label: 'TCO Total',
      values: vehicles.map((v) => get(v, 'tco', 'total_tco')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'TCO Monthly',
      values: vehicles.map((v) => get(v, 'tco', 'monthly_equivalent')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'TCO Annual',
      values: vehicles.map((v) => get(v, 'tco', 'annualized')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'TCO Per Mile',
      values: vehicles.map((v) => get(v, 'tco', 'per_mile')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'TCO 3 Year',
      values: vehicles.map((v) => get(v, 'tco', 'three_year_tco')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'TCO 5 Year',
      values: vehicles.map((v) => get(v, 'tco', 'five_year_tco')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'Depreciation Total',
      values: vehicles.map((v) => get(v, 'depreciation', 'total_depreciation')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'Depreciation Share',
      values: vehicles.map((v) => get(v, 'tco', 'depreciation_share')),
      format: 'percent',
      lowerBetter: true,
    },
    {
      label: 'Total Interest',
      values: vehicles.map((v) => get(v, 'financing', 'total_interest_to_sale')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'Interest Share',
      values: vehicles.map((v) => get(v, 'tco', 'interest_share')),
      format: 'percent',
      lowerBetter: true,
    },
    {
      label: 'Operating Costs',
      values: vehicles.map((v) => get(v, 'operating', 'horizon_total')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      // Phase 21H: Argonne ANL/ESD-22/2 residual reclamation. Higher = better
      // (less depreciation, more value retained). Shown as a credit line.
      label: 'Residual Value Reclaimed',
      values: vehicles.map((v) => get(v, 'tco', 'residual_value_reclaimed')),
      format: 'currency',
      lowerBetter: false,
      isCredit: true,
      tooltip:
        'Estimated value retained at end of ownership period, based on vehicle class, ' +
        'powertrain, age, and accumulated mileage. Reduces total TCO. ' +
        'Source: Argonne National Laboratory ANL/ESD-22/2.',
    },
    {
      // Phase 23: Section 179 + bonus depreciation tax benefit. Year-1 federal
      // cash credit. Higher = better (larger reduction in effective acquisition
      // cost). Hidden in render when all values are 0 (tax-exempt scenarios or
      // ineligible vehicles).
      label: 'Tax Benefit (Sec. 179 + Bonus)',
      values: vehicles.map((v) => get(v, 'tco', 'tax_benefit')),
      format: 'currency',
      lowerBetter: false,
      isCredit: true,
      tooltip:
        'First-year federal tax deduction from Section 179 expensing and bonus ' +
        'depreciation, multiplied by your marginal tax rate. Reduces effective ' +
        'acquisition cost. Federal only — state conformity and MACRS years 2–7 ' +
        'are not modeled. Sources: IRC §179, IRC §168(k), IRS Rev. Proc. 2024-21.',
    },
    {
      label: 'Operating Share',
      values: vehicles.map((v) => get(v, 'tco', 'operating_share')),
      format: 'percent',
      lowerBetter: true,
    },
    {
      label: 'PV Discounted Total',
      values: vehicles.map((v) => get(v, 'tco', 'pv_total')),
      format: 'currency',
      lowerBetter: true,
    },
    {
      label: 'Opportunity Cost',
      values: vehicles.map((v) => get(v, 'tco', 'opportunity_cost_estimate')),
      format: 'currency',
      lowerBetter: true,
      tooltip:
        'Investment income foregone on capital tied up in the vehicle, computed as ' +
        'average committed capital × investment return rate × ownership years. ' +
        'Average committed capital = (cash committed at purchase + residual at sale) ÷ 2, ' +
        'which nets out value recovered through resale rather than treating the full ' +
        'purchase price as capital lost for the entire ownership period.',
    },
    {
      label: 'Loaded TCO',
      values: vehicles.map((v) => get(v, 'tco', 'loaded_tco')),
      format: 'currency',
      lowerBetter: true,
    },
  ];
}

function cellClass(row: Row, idx: number): string {
  const nums = row.values.filter((v) => v !== null) as number[];
  const isLoaded = row.label === 'Loaded TCO';
  if (nums.length < 2) return isLoaded ? 'font-bold' : '';
  const best = row.lowerBetter ? Math.min(...nums) : Math.max(...nums);
  const worst = row.lowerBetter ? Math.max(...nums) : Math.min(...nums);
  const v = row.values[idx];
  if (v === null) return '';
  if (v === best) return isLoaded ? 'text-emerald-400 font-bold' : 'text-emerald-400 font-semibold';
  if (v === worst) return isLoaded ? 'text-red-400 font-bold' : 'text-red-400 font-semibold';
  return isLoaded ? 'font-bold' : '';
}

function subCellClass(values: (number | null)[], idx: number, lowerBetter: boolean): string {
  const nums = values.filter((v) => v !== null) as number[];
  if (nums.length < 2) return '';
  const best = lowerBetter ? Math.min(...nums) : Math.max(...nums);
  const worst = lowerBetter ? Math.max(...nums) : Math.min(...nums);
  const v = values[idx];
  if (v === null) return '';
  if (v === best) return 'text-emerald-400 font-semibold';
  if (v === worst) return 'text-red-400 font-semibold';
  return '';
}

function formatValue(row: Row, v: number | null, rowLabel: string): string {
  if (v === null) return '—';
  if (rowLabel === 'Horizon Months') return String(v);
  if (row.format === 'percent') return fmtPct(v);
  return fmt$(v);
}

// Phase 22.3 / 22.5: cost-line annualization convention.
//
// Rows below represent a horizon-total dollar value, so an annualized
// hint ("$X/yr") is informative. This is the *individual-mode* table —
// every cell already reflects a single vehicle, so per-vehicle subtext
// would be redundant.
//
// Rows NOT in this set deliberately have no annualized subtext:
//   - Purchase Price / Residual Value Reclaimed: one-time at t=0 / t=N,
//     never annualized.
//   - Per-mile / monthly / annual rates: already in unit terms.
//   - Share rows: percentages.
//
// The fleet-mode equivalent (apps/web/src/pages/ScenarioComparePage.tsx)
// uses the same convention, expressed via a per-row `kind` field on the
// COST_LINES table because fleet rows mix annual and lifetime values.
const ANNUALIZED_HINT_ROWS = new Set([
  'TCO Total',
  'TCO 3 Year',
  'TCO 5 Year',
  'Depreciation Total',
  'Total Interest',
  'Operating Costs',
  'Loaded TCO',
]);

// ── Item 3: Operating breakdown sub-rows ──────────────────────────────────────

interface BreakdownSubRow {
  label: string;
  key: keyof OperatingBreakdown;
  lowerBetter: boolean;
}

const BREAKDOWN_SUB_ROWS: BreakdownSubRow[] = [
  { label: 'Fuel / Electricity', key: 'fuel_cost_total', lowerBetter: true },
  { label: 'Insurance', key: 'insurance_total', lowerBetter: true },
  { label: 'Registration', key: 'registration_total', lowerBetter: true },
  { label: 'Maintenance', key: 'maintenance_total', lowerBetter: true },
  { label: 'Tires', key: 'tire_cost_total', lowerBetter: true },
  { label: 'Repairs', key: 'repair_cost_total', lowerBetter: true },
];

function getBreakdown(v: VehicleResult): OperatingBreakdown | null {
  const op = v.operating as Record<string, unknown> | null;
  if (!op) return null;
  const bd = op['operating_breakdown'] as Record<string, unknown> | null;
  if (!bd) return null;
  return {
    fuel_cost_total: Number(bd['fuel_cost_total'] ?? 0),
    insurance_total: Number(bd['insurance_total'] ?? 0),
    registration_total: Number(bd['registration_total'] ?? 0),
    maintenance_total: Number(bd['maintenance_total'] ?? 0),
    tire_cost_total: Number(bd['tire_cost_total'] ?? 0),
    repair_cost_total: Number(bd['repair_cost_total'] ?? 0),
  };
}

export default function TCOSummaryTable({ vehicles, horizonMonths, purchasePrices }: Props) {
  // Phase 24: federal first-year tax benefit is a fleet-only surface — the
  // Sec. 179 / bonus deduction is a business-tax mechanic, not a personal
  // purchase decision input.
  const isFleet = useAppModeStore((s) => s.isFleet);
  // Phase 23: also hide when no vehicle has a benefit (tax-exempt scenarios
  // or all-ineligible fleets).
  const rows = buildRows(vehicles, horizonMonths).filter((row) => {
    if (row.label !== 'Tax Benefit (Sec. 179 + Bonus)') return true;
    if (!isFleet) return false;
    return row.values.some((v) => (v ?? 0) > 0);
  });
  const [opExpanded, setOpExpanded] = useState(false);

  const breakdowns = vehicles.map(getBreakdown);

  return (
    <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid var(--border)' }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: 'var(--panel2)' }}>
            <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider w-44" style={{ color: 'var(--white-dim)', fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.2em' }}>
              Metric
            </th>
            {vehicles.map((v, i) => (
              <th key={v.vehicle_id} className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: VEHICLE_COLORS[i] }}>
                {v.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const isOpRow = row.label === 'Operating Costs';
            const isHorizonRow = row.label === 'Horizon Months';
            return (
              <>
                <tr
                  key={row.label}
                  className={`border-t border-[var(--border)] ${ri % 2 === 0 ? 'bg-[var(--panel)]' : 'bg-[var(--panel2)]'} ${isOpRow ? 'cursor-pointer hover:bg-[var(--border)]' : ''}`}
                  onClick={isOpRow ? () => setOpExpanded(!opExpanded) : undefined}
                >
                  <td className={`px-4 py-2.5 text-xs ${row.label === 'Loaded TCO' ? 'font-bold' : ''}`} style={{ color: row.label === 'Loaded TCO' ? 'var(--white)' : 'var(--white-dim)' }}>
                    <div className="flex items-center gap-1.5">
                      {isOpRow && (
                        <svg
                          className={`w-3.5 h-3.5 transition-transform shrink-0 ${opExpanded ? 'rotate-90' : ''}`} style={{ color: 'var(--white-dim)' }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                      {row.label}
                      {row.tooltip && (
                        <span
                          tabIndex={0}
                          role="button"
                          aria-label={row.tooltip}
                          className="relative inline-flex group items-center justify-center w-[18px] h-[18px] rounded-full text-[10px] font-bold leading-none cursor-help align-middle"
                          style={{ background: 'var(--panel)', color: 'var(--white-dim)', border: '1px solid var(--border)' }}
                        >
                          i
                          <span
                            role="tooltip"
                            className="invisible group-hover:visible group-focus:visible opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity absolute left-0 top-full mt-2 w-72 z-30 rounded-lg px-3 py-2 text-xs leading-relaxed shadow-lg pointer-events-none normal-case tracking-normal"
                            style={{ background: 'var(--panel2)', border: '1px solid var(--border)', color: 'var(--white)', fontFamily: 'inherit', fontWeight: 400 }}
                          >
                            {row.tooltip}
                          </span>
                        </span>
                      )}
                    </div>
                  </td>
                  {vehicles.map((v, ci) => {
                    const value = row.values[ci];
                    const showAnnualized =
                      ANNUALIZED_HINT_ROWS.has(row.label)
                      && value !== null
                      && horizonMonths[ci] > 0;
                    const annualizedText = showAnnualized
                      ? fmtPerYear(value, horizonMonths[ci])
                      : null;
                    return (
                      <td
                        key={v.vehicle_id}
                        className={`text-right px-4 py-2.5 font-mono text-sm ${cellClass(row, ci)}`}
                        style={row.isCredit ? { color: 'var(--tco-accent)' } : undefined}
                      >
                        <div>
                          {row.isCredit && (value ?? 0) > 0 ? '−' : ''}
                          {formatValue(row, value, row.label)}
                        </div>
                        {annualizedText && (
                          <div className="text-[10px] font-normal" style={{ color: 'var(--white-dim)' }}>
                            {annualizedText}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Purchase Price row — inserted after Horizon Months, no color coding */}
                {isHorizonRow && (
                  <tr key="purchase-price" className={`border-t border-[var(--border)] ${(ri + 1) % 2 === 0 ? 'bg-[var(--panel)]' : 'bg-[var(--panel2)]'}`}>
                    <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--white-dim)' }}>Purchase Price</td>
                    {vehicles.map((v, ci) => (
                      <td key={v.vehicle_id} className="text-right px-4 py-2.5 font-mono text-sm" style={{ color: 'var(--white)' }}>
                        {'$' + Math.round(purchasePrices[ci] ?? 0).toLocaleString('en-US')}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Expandable operating breakdown sub-rows */}
                {isOpRow && opExpanded && BREAKDOWN_SUB_ROWS.map((sub) => {
                  const subValues = breakdowns.map((bd) =>
                    bd ? bd[sub.key] : null
                  );
                  return (
                    <tr key={sub.key} className="border-t border-[var(--border)]" style={{ background: 'var(--panel2)' }}>
                      <td className="px-4 py-2 text-xs pl-9" style={{ color: 'var(--white-dim)' }}>
                        {sub.label}
                      </td>
                      {vehicles.map((v, ci) => (
                        <td
                          key={v.vehicle_id}
                          className={`text-right px-4 py-2 font-mono text-xs ${subCellClass(subValues, ci, sub.lowerBetter)}`}
                        >
                          <div>{fmtPerYear(subValues[ci], horizonMonths[ci])}</div>
                          <div className="text-xs font-normal" style={{ color: 'var(--white-dim)' }}>{fmtTotal(subValues[ci])}</div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
